import type { Editor } from '@tiptap/react';

export const cleanPastedHTML = (input: string) => {
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(input, 'text/html');
        const body = doc.body;

        let current = body.firstElementChild;

        while (current && current.nextElementSibling) {
            const next = current.nextElementSibling;

            if (current.tagName === 'P' && next.tagName === 'P') {
                const currentText = current.textContent || '';
                const nextText = next.textContent || '';

                const hasTerminal = /[.!?:;]\s*$/.test(currentText);
                const isListStart = /^(\d+[.)]|[•-])\s/.test(nextText);
                const isCurrentEmpty = currentText.trim().length === 0;
                const isNextEmpty = nextText.trim().length === 0;

                if (!hasTerminal && !isListStart && !isCurrentEmpty && !isNextEmpty) {
                    const endsWithHyphen = /-\s*$/.test(currentText);

                    if (!endsWithHyphen && !/\s$/.test(currentText)) {
                        current.appendChild(doc.createTextNode(' '));
                    }

                    while (next.firstChild) {
                        current.appendChild(next.firstChild);
                    }

                    next.remove();
                    continue;
                }
            }

            current = next;
        }

        return body.innerHTML;
    } catch (error) {
        console.error('Error parsing pasted HTML:', error);
        return input;
    }
};

export const hasMeaningfulHtmlContent = (html: string) =>
    html.replace(/<[^>]*>/g, '').trim().length > 0;

export const hasMeaningfulTextContent = (text: string) => text.trim().length > 0;

// Map a section-global text offset (characters from the start of the whole
// section, as clicked in the paginated read view) to an editor position + the
// top-level block that contains it. Text offset is used rather than a block
// index because the read view paginates the section across pages — and may even
// split a paragraph mid-way — so DOM block indices don't line up with the
// editor's whole-section document. Total text is conserved across the split, so
// the character offset does line up.
export const resolveEditorPositionFromTextOffset = (
    editor: Editor,
    textOffset: number,
): { pos: number; blockIndex: number; nodeFrom: number; nodeTo: number } => {
    const { doc } = editor.state;
    const childCount = doc.childCount;

    if (childCount === 0) return { pos: 0, blockIndex: 0, nodeFrom: 0, nodeTo: 0 };

    let remaining = Math.max(textOffset, 0);
    let blockIndex = 0;
    let inner = 0;

    for (let i = 0; i < childCount; i += 1) {
        const len = doc.child(i).textContent.length;
        blockIndex = i;

        if (remaining <= len) {
            inner = remaining;
            break;
        }

        remaining -= len;
        inner = len;
    }

    let nodeFrom = 0;
    for (let i = 0; i < blockIndex; i += 1) {
        nodeFrom += doc.child(i).nodeSize;
    }

    const node = doc.child(blockIndex);
    const clampedInner = Math.min(Math.max(inner, 0), node.content.size);

    // nodeFrom is the position before the block; its text starts at nodeFrom + 1.
    return {
        pos: nodeFrom + 1 + clampedInner,
        blockIndex,
        nodeFrom,
        nodeTo: nodeFrom + node.nodeSize,
    };
};

// The rendered DOM element for the Nth top-level block, so we can scroll/flash
// the actual paragraph. Layout-free (no position math). TrailingNode adds a
// trailing empty <p>, so children count = blocks + 1; the clamp handles it.
export const getTopLevelBlockElement = (editor: Editor, blockIndex: number): HTMLElement | null => {
    const children = editor.view.dom.children;

    if (children.length === 0) return null;

    const index = Math.min(Math.max(blockIndex, 0), children.length - 1);

    return (children[index] as HTMLElement) ?? null;
};
